import { module } from 'modujs';
import IcecastMetadataPlayer from 'icecast-metadata-player';

const DEBUG_MODULE = false;
const DEBUG_PLAYER = false;

/**
 * For unknown reasons, Icecast Metadata Player's `retryTimeout`
 * is not working.
 */
const MAX_RETRIES = 5;

/** Radio Module States */
const RADIO_STATE = {
    ERRORED:   'errored',
    IDLE:      'idle',
    PLAYING:   'playing',
    SWITCHING: 'switching',
};

/** Icecast Metadata Player States */
const STREAMER_STATE = {
    LOADING:   'loading',
    PLAYING:   'playing',
    STOPPING:  'stopping',
    STOPPED:   'stopped',
    RETRYING:  'retrying',
    SWITCHING: 'switching',
};

/**
 * Radio Player using Icecast Metadata Player
 */
export default class extends module {
    /** @type {?IcecastMetadataPlayer} */
    #audioPlayer;

    /** @type {?AudioAnalyser} */
    #audioAnalyser;

    /** @type {?AudioVisualiser} */
    #audioVisualiser;

    /** @type {boolean} */
    #busy = false;

    /** @type {boolean} */
    #disabled = false;

    constructor(m) {
        super(m);

        // UI
        this.$playbackButton = this.$('playback')[0];
        this.$channelSelect = this.$('channel')[0];
        this.$metadata = this.$('metadata')[0];
        this.$draggable = this.$('draggable')[0];
        this.$canvas = this.$('canvas')[0];
        this.$box = this.$('box')[0];

        this.state = RADIO_STATE.IDLE;
        this.attempts = 0;
        this.isDragging = false;
        this.isMinimized = false;
        this.offsetX = 0;
        this.offsetY = 0;

        // Events
        this.events = {
            'change': {
                'channel': 'handleChangeChannel',
            },
            'click': {
                'playback': 'handleTogglePlayback',
                'minimize': 'handleToggleMinimize',
            },
        };

        this.onMouseMoveBind = this.onMouseMove.bind(this);
        this.onMouseUpBind   = this.onMouseUp.bind(this);
        this.onMouseDownBind = this.onMouseDown.bind(this);

        if (DEBUG_MODULE) {
            window.radioPlayer = this;
        }
    }

    get busy() {
        return this.#busy;
    }

    set busy(busy) {
        if (busy) {
            this.el.setAttribute('aria-busy', 'true');
        } else {
            this.el.setAttribute('aria-busy', 'false');
        }

        return this.#busy = !!busy;
    }

    get disabled() {
        return this.#disabled;
    }

    set disabled(disabled) {
        if (disabled) {
            this.$playbackButton.setAttribute('aria-disabled', 'true');
            this.$channelSelect.setAttribute('aria-disabled', 'true');
        } else {
            this.$playbackButton.removeAttribute('aria-disabled');
            this.$channelSelect.removeAttribute('aria-disabled');
        }

        return this.#disabled = !!disabled;
    }

    /**
     * @return {IcecastMetadataPlayer}
     */
    get audioPlayer() {
        return this.#audioPlayer ?? this.createAudioPlayer();
    }

    /**
     * @return {AudioAnalyser}
     */
    get audioAnalyser() {
        return this.#audioAnalyser ?? this.createAudioAnalyser();
    }

    /**
     * @return {AudioVisualiser}
     */
    get audioVisualiser() {
        return this.#audioVisualiser ?? this.createAudioVisualiser();
    }

    // ==========================================================================
    // Lifecycle
    // ==========================================================================
    init() {
        this.bindEvents();
        this.disabled = false;
    }

    destroy() {
        super.destroy();
        this.#audioPlayer?.stop?.();
        this.unbindEvents();
        this.el.classList.remove('is-dragging', 'is-playing');
    }

    bindEvents() {
        this.$draggable.addEventListener('mousedown', this.onMouseDownBind);
    }

    unbindEvents() {
        this.$draggable.removeEventListener('mousedown', this.onMouseDownBind);
    }

    // =========================================================================
    // Callbacks
    // =========================================================================
    async handleTogglePlayback() {
        if (!this.canPlay()) {
            this.disabled = true;
            return;
        }

        if (this.audioPlayer.state === STREAMER_STATE.STOPPED) {
            await this.audioPlayer.play();
        } else if (this.audioPlayer.state !== STREAMER_STATE.STOPPED && this.audioPlayer.state !== STREAMER_STATE.STOPPING) {
            await this.audioPlayer.stop();
        }
    }

    async handleChangeChannel() {
        if (!this.canPlay()) {
            this.disabled = true;
            return;
        }

        this.state = RADIO_STATE.SWITCHING;

        this.busy = true;
        this.disabled = true;

        /** @todo Move label to HTML. */
        this.$metadata.textContent = 'Changing channel';

        await this.audioPlayer.stop();

        const channel = this.getSelectedChannel();
        DEBUG_MODULE && console.log('[Radio]', 'Changing channel:', channel);

        this.resetAudioComponents(channel);

        this.audioPlayer.play();
    }

    handleToggleMinimize() {
        this.resetPosition();

        this.el.classList.toggle('is-minimized');

        this.isMinimized = !this.isMinimized;
    }

    onError = (message, error) => {
        console.log('[Radio]', 'Error:', message, error);

        this.state = RADIO_STATE.ERRORED;

        this.busy = false;
        this.disabled = true;

        this.$metadata.textContent = error.message;
    }

    onLoad = () => {
        this.attempts++;

        DEBUG_MODULE && console.log('[Radio]', 'Loading');

        this.busy = true;
        this.disabled = true;

        /** @todo Move label to HTML. */
        this.$metadata.textContent = (
            (this.attempts === 1 && this.getSelectedChannel() === this.audioPlayer.endpoint)
            ? 'Reconnecting...'
            : 'Connecting...'
        );
    }

    onMetadata = (metadata) => {
        DEBUG_MODULE && console.log('[Radio]', 'Metadata:', metadata);
        this.$metadata.textContent = metadata.StreamTitle || 'Playing';
    }

    onMetadataEnqueue = (metadata) => {
        DEBUG_MODULE && console.log('[Radio]', 'Metadata Enqueue:', metadata);
        this.$metadata.textContent = metadata.StreamTitle || 'Playing';
    }

    onPlay = async () => {
        DEBUG_MODULE && console.log('[Radio]', 'Play');
        this.el.classList.add('is-playing');
        this.$playbackButton.setAttribute('aria-pressed', 'true');

        this.state = RADIO_STATE.PLAYING;

        this.busy = false;
        this.disabled = false;
        this.attempts = 0;

        this.audioVisualiser.resume();
    }

    onStop = () => {
        DEBUG_MODULE && console.log('[Radio]', 'Stop');
        this.el.classList.remove('is-playing');
        this.$playbackButton.setAttribute('aria-pressed', 'false');

        if (this.state !== RADIO_STATE.SWITCHING) {
            this.state = RADIO_STATE.IDLE;

            this.busy = false;
            this.disabled = false;

            this.$metadata.textContent = '';
        }

        this.audioVisualiser.suspend();
    }

    onRetry = () => {
        this.attempts++;

        DEBUG_MODULE && console.log('[Radio]', 'Retrying:', `${this.attempts} / ${MAX_RETRIES}`);
        if (this.attempts >= MAX_RETRIES) {
            this.audioPlayer.stop().finally(this.onRetryTimeout);
        }

        this.busy = true;
        this.disabled = true;

        /** @todo Move label to HTML. */
        this.$metadata.textContent = (
            (this.getSelectedChannel() === this.audioPlayer.endpoint)
            ? 'Trying to reconnect'
            : 'Trying to connect'
        );
    }

    onRetryTimeout = () => {
        DEBUG_MODULE && console.log('[Radio]', 'Retries Failed');
        this.state = RADIO_STATE.ERRORED;

        this.busy = false;
        this.disabled = true;

        /** @todo Move label to HTML. */
        this.$metadata.textContent = 'Could not connect';
    }

    onWarn = (...messages) => {
        console.log('[Radio]', 'Warning:', messages);
        // this.$metadata.textContent = message[0];
    }

    // =========================================================================
    // Methods
    // =========================================================================
    resetAudioComponents() {
        this.#audioPlayer = null;
        this.#audioAnalyser = null;
        this.#audioVisualiser = null;
    }

    /**
     * @param  {Array<string>|string} [channel]
     * @return {IcecastMetadataPlayer}
     */
    createAudioPlayer(channel = null) {
        return this.#audioPlayer = new IcecastMetadataPlayer(
            (channel ?? this.getSelectedChannel()),
            {
                enableLogging:     DEBUG_PLAYER,
                onError:           this.onError,
                onLoad:            this.onLoad,
                onMetadata:        this.onMetadata,
                onMetadataEnqueue: this.onMetadataEnqueue,
                onPlay:            this.onPlay,
                onRetry:           this.onRetry,
                onRetryTimeout:    this.onRetryTimeout,
                onStop:            this.onStop,
                onWarn:            this.onWarn,
                retryTimeout:      10,
            }
        );
    }

    /**
     * @return {AudioAnalyser}
     */
    createAudioAnalyser() {
        return this.#audioAnalyser = new AudioAnalyser(
            this.audioPlayer.audioElement
        );
    }

    /**
     * @return {AudioVisualiser}
     */
    createAudioVisualiser() {
        return this.#audioVisualiser = new AudioVisualiser(
            this.$canvas,
            this.audioAnalyser,
            () => this.audioPlayer.state
        );
    }

    getSelectedChannel() {
        return this.$channelSelect?.value || null;
    }

    getChannels() {
        return Array.from(this.$channelSelect?.options).map((option) => option.value);
    }

    hasChannels() {
        return this.$channelSelect?.options.length > 0;
    }

    canPlay() {
        return (
            this.state !== RADIO_STATE.ERRORED &&
            this.state !== RADIO_STATE.SWITCHING &&
            !this.disabled &&
            (this.attempts < MAX_RETRIES) &&
            this.hasChannels());
    }

    async play() {
        if (!this.canPlay()) {
            this.disabled = true;
            return await Promise.reject(new Error(`Player unavailable`));
        }

        return await this.audioPlayer.play();
    }

    async stop() {
        if (!this.canPlay()) {
            this.disabled = true;
            return await Promise.reject(new Error(`Player unavailable`));
        }

        return await this.audioPlayer.stop();
    }

    // ==========================================================================
    // Mouse drag
    // ==========================================================================
    onMouseDown(e) {
        if(this.isMinimized) {
            this.handleToggleMinimize();
            return;
        }

        this.isDragging = true;

        // Get the initial mouse position relative to the element's top-left corner
        this.offsetX = e.clientX - this.$box.offsetLeft;
        this.offsetY = e.clientY - this.$box.offsetTop;

        // Add event listeners to handle dragging
        document.addEventListener('mousemove', this.onMouseMoveBind);
        document.addEventListener('mouseup', this.onMouseUpBind);

        this.el.classList.add('is-dragging');
    }

    onMouseMove(e) {
        if (this.isDragging && !this.isMinimized) {
            // Calculate the new position of the element
            const newLeft = e.clientX - this.offsetX;
            const newTop = e.clientY - this.offsetY;

            // Apply the new position
            this.$box.style.left = `${newLeft}px`;
            this.$box.style.top = `${newTop}px`;
        }
    }

    onMouseUp() {
        this.isDragging = false;

        // Remove the mousemove and mouseup event listeners when dragging stops
        document.removeEventListener('mousemove', this.onMouseMoveBind);
        document.removeEventListener('mouseup', this.onMouseUpBind);

        this.el.classList.remove('is-dragging');
    }

    resetPosition() {
        this.$box.style.left = '';
        this.$box.style.top = '';
    }
}

/**
 * Facade for AudioContext to create and connect
 * an audio analyser to a media element.
 */
class AudioAnalyser {
    /** @type {AudioNode} */
    #analyser;

    /** @type {AudioContext} */
    #context;

    /** @type {number} */
    #frequencySize;

    /** @type {MediaElementAudioSourceNode} */
    #source;

    /**
     * @param {HTMLMediaElement} mediaElement
     */
    constructor(mediaElement) {
        this.#context = new AudioContext();

        this.#analyser = this.#context.createAnalyser();
        // Number of data points (more points = smoother,
        // but more performance-heavy). Defaults to 2048.
        this.#analyser.fftSize = 256;

        this.#source = this.#context.createMediaElementSource(mediaElement);
        this.#source.connect(this.#analyser);
        this.#analyser.connect(this.#context.destination);

        // Total number of data points available to the context's `sampleRate`.
        // This is half of the value of the analyser's `fftSize`.
        this.#frequencySize = this.#analyser.frequencyBinCount;
    }

    /**
     * Alias of {@see AudioNode.getByteFrequencyData}.
     *
     * @param {Uint8Array} array
     */
    getByteFrequencyData(array) {
        this.#analyser.getByteFrequencyData(array);
    }

    get analyser() {
        return this.#analyser;
    }

    get context() {
        return this.#context;
    }

    get frequencySize() {
        return this.#frequencySize;
    }
}

class AudioVisualiser {
    /** @type {AudioAnalyser} */
    #analyser;

    /** @type {HTMLCanvasElement} */
    #canvas;

    /** @type {?function} */
    #frame;

    /** @type {number} */
    #request;

    /** @type {(():string)} */
    #state;

    /**
     * @param {HTMLCanvasElement} canvas
     * @param {AudioAnalyser}     analyser
     * @param {(():string)}       state
     */
    constructor(canvas, analyser, state) {
        this.#canvas   = canvas;
        this.#analyser = analyser;
        this.#state    = state;
    }

    resume() {
        if (this.#state() === STREAMER_STATE.PLAYING) {
            return;
        }

        if (!this.#frame) {
            this.draw();
        }

        this.#request = window.requestAnimationFrame(this.#frame);
    }

    suspend() {
        if (this.#state() !== STREAMER_STATE.PLAYING) {
            return;
        }

        if (this.#request) {
            window.cancelAnimationFrame(this.#request);
        }

        this.#frame = null;
    }

    draw() {
        const canvasContext = this.#canvas.getContext('2d');
        const canvasSize    = this.#canvas.getBoundingClientRect();
        const deviceScale   = window.devicePixelRatio || 1;

        this.#canvas.width  = Math.floor(canvasSize.width * deviceScale);
        this.#canvas.height = Math.floor(canvasSize.height * deviceScale);

        const canvasWidth = this.#canvas.width;
        const canvasHeight = this.#canvas.height;

        canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);

        const frequencyData = new Uint8Array(this.#analyser.frequencySize);

        // Create a function to draw the frequency bars
        this.#frame = () => {
            this.#analyser.getByteFrequencyData(frequencyData);

            canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);

            const barWidth = Math.floor(canvasWidth / this.#analyser.frequencySize) * 3;
            let x = 0;

            for (let i = 0; i < this.#analyser.frequencySize; i++) {
                const barHeight = Math.floor(frequencyData[i] / 2);

                canvasContext.fillStyle = '#403F3B';
                canvasContext.fillRect(
                    x,
                    (canvasHeight - Math.floor(barHeight / 2)),
                    barWidth,
                    barHeight
                );

                x += barWidth + 1;
            }

            this.#request = window.requestAnimationFrame(this.#frame);
        };
    }
}
